import React, { useState } from "react";

// @material-ui/icons
import { Edit } from "components/MaterialSymbol/MaterialIcons";

// core components
import ReactTable from "components/ReactTable/ReactTable.js";
import Modal from "components/Modal/Modal.js";
import Notification from "components/Notification/Notification.js";
import DeleteButton from "components/DeleteButton/DeleteButton.js";
import Button from "components/CustomButtons/Button.js";
import PageLayout from "layouts/PageLayout";
import TextActionButton from "components/TextActionButton/TextActionButton";

// custom components & hooks
import ManagedDnsZoneForm from "./ManagedDnsZoneForm.js";
import ManagedDnsZonesService from "../../services/ManagedDnsZonesService";
import useManagedDnsZonesSwr from "hooks/useManagedDnsZonesSwr";

// assets
import awsLogo from "assets/img/providers/aws-route53.png";
import godaddyLogo from "assets/img/providers/godaddy.png";

/**
 * ManagedDnsZones
 * Main view with local provider assets integrated in the table.
 */
export default function ManagedDnsZones({ ...otherProps }) {
    const { zones, mutate, isLoading } = useManagedDnsZonesSwr();
    const [activeZone, setActiveZone] = useState(null);
    const [notification, setNotification] = useState(null);

    const openCreateModal = () => setActiveZone({});
    const openEditModal = (zone) => setActiveZone(zone);
    const closeModal = () => setActiveZone(null);

    const handleFormSuccess = (message) => {
        mutate();
        closeModal();
        setNotification({ color: "success", message });
    };

    const handleDelete = async (id) => {
        try {
            await ManagedDnsZonesService.remove(id);
            mutate();
            setNotification({ color: "success", message: "DNS Management zone removed" });
        } catch (error) {
            console.error("Deletion failed:", error);
            setNotification({ color: "danger", message: "Failed to remove zone" });
        }
    };

    const columns = [
        {
            Header: "Base Domain",
            accessor: "baseDomain",
            className: "text-bold"
        },
        {
            Header: "Provider",
            accessor: "dnsProvider",
            Cell: ({ value }) => {
                const isAWS = value === 'route53';
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img
                            src={isAWS ? awsLogo : godaddyLogo}
                            alt={value}
                            style={{ height: '20px', width: '20px', objectFit: 'contain' }}
                        />
                        <span>{isAWS ? 'AWS Route53' : 'GoDaddy'}</span>
                    </div>
                );
            }
        },
        {
            Header: "Environment Mappings",
            accessor: "providerConfig",
            Cell: ({ value: config }) => (
                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                    <strong>AK:</strong> {config?.access_key_env || '-'} <br />
                    <strong>SK:</strong> {config?.secret_key_env || '-'}
                </div>
            )
        },
        {
            Header: "Actions",
            accessor: "id",
            sortable: false,
            filterable: false,
            Cell: ({ row }) => {
                const zone = row.original;
                return (
                    <div className="actions-right">
                        <Button
                            justIcon round simple
                            color="warning"
                            onClick={() => openEditModal(zone)}
                        >
                            <Edit />
                        </Button>
                        <DeleteButton
                            title="DNS Management Rule"
                            handleClick={() => handleDelete(zone.id)}
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <PageLayout
            showSelectDealer={false}
            customComponent={
                <TextActionButton onClick={openCreateModal}>+ Setup New Zone</TextActionButton>
            }
            {...otherProps}
        >
            {notification && (
                <Notification
                    open={!!notification}
                    color={notification.color}
                    message={notification.message}
                    onClose={() => setNotification(null)}
                />
            )}

            <Modal
                open={!!activeZone}
                modalTitle={activeZone?.id ? "Refine DNS Configuration" : "Initialize New DNS Zone"}
                handleClose={closeModal}
            >
                <ManagedDnsZoneForm
                    initialValues={activeZone || {}}
                    onSuccess={handleFormSuccess}
                    onCancel={closeModal}
                />
            </Modal>

            <ReactTable
                columns={columns}
                data={zones}
                showPagination={true}
                defaultPageSize={10}
                LoadingComponent={() => isLoading ? <div style={{ padding: '40px', textAlign: 'center' }}>Synchronizing...</div> : null}
            />
        </PageLayout>
    );
}
